import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const sections = [
  {
    id: 'intro',
    title: '1. Introduction',
    content: `Weddyfyp is an online matrimonial platform committed to safeguarding your privacy and providing secure and personalised matrimonial services. This Privacy Policy applies to all websites and mobile applications operated under the Weddyfyp platform, operated by Roald Software Solutions Private Limited ("Company", "we", "us", or "our").

By accessing or using our Platform, and/or by voluntarily providing your information, you agree to the collection, use, storage, sharing, and processing of your data as described in this Policy. If you do not agree with the terms outlined herein, please refrain from using our website or mobile applications.

We are committed to handling your information responsibly and transparently. If you have questions about anything in this Policy, you are welcome to write to us at info@weddyfyp.com before using the Platform.`,
  },
  {
    id: 'collect',
    title: '2. Information We Collect',
    content: `To access and use our services, you may be required to provide personal information, which may include but is not limited to:

i. Profile photo
ii. Full name and date of birth
iii. Email address and contact details (phone/mobile)
iv. Educational qualifications
v. Identity verification documents (with your explicit consent)
vi. Login credentials (username and password)
vii. Religious details (e.g., religion, caste, star, raasi, gothram)
viii. Horoscope information
ix. Family information (e.g., family values, family type, parental and sibling details)
x. Employment and professional details (e.g., education, occupation, organisation, income)

If you establish a payment account with us, we may also collect billing information, such as:
i. Credit/debit card details (number and expiry)
ii. Billing address

Additional data collected through our website or apps includes:
i. Device information and specifications
ii. IP address and geographic location
iii. Log files and device ID

We also use cookies to enhance your browsing experience. Cookies help us store login credentials and track user interactions. Third-party cookies may also be present, and we do not control their usage. Please see the Cookies section below for more detail.`,
  },
  {
    id: 'use',
    title: '3. How We Use Your Information',
    content: `Your personal data may be used for purposes including but not limited to:

i. Account verification and login authentication
ii. Creating and maintaining your member profile
iii. Verifying your identity using your mobile number
iv. Showing your profile to other eligible members on the Platform
v. Suggesting potential matches based on your stated preferences
vi. Enabling and delivering in-app messaging between members
vii. Data analysis and user experience improvement
viii. Marketing research and promotional effectiveness
ix. Prevention and detection of fraud
x. Site and app performance evaluation
xi. Personalised service delivery
xii. Processing and confirming payments for premium features
xiii. Responding to support requests, complaints, and enquiries
xiv. Complying with our legal and regulatory obligations
xv. Sending you service-related notifications (match alerts, profile reminders)

We use your IP address to manage our servers, identify technical issues, and derive broad demographic data.

We do not use your data to serve targeted advertising from third parties. We do not sell your personal information.`,
  },
  {
    id: 'advertising',
    title: '4. Advertising ID & Third-Party SDKs',
    content: `Our mobile application uses the Google Advertising ID (AAID) and third-party SDKs such as Firebase Analytics. These technologies may collect and use the Advertising ID for purposes including:

i. Advertising & Marketing: To measure ad performance, track app installs from ad campaigns, and deliver relevant promotions.

ii. Analytics: To understand how users interact with our app and improve user experience.

The Advertising ID is a resettable, non-permanent device identifier that does not directly reveal your personal information. You can reset or limit its use at any time from your Android device settings:
Settings → Privacy → Ads

These third-party services operate under their own privacy policies. We recommend reviewing the privacy policies of Google and any other third-party services you interact with through our Platform.

We do not share your matrimonial profile data, photographs, or personal details with advertising SDKs.`,
  },
  {
    id: 'visibility',
    title: '5. What Other Members Can See',
    content: `Your profile on Weddyfyp is visible to other registered, verified members. Here is what they can and cannot see:

Visible to other members:
• Your name (or the display name you choose), age, location, religion, community, education, occupation, and photographs you upload
• Your bio and partner preferences
• Whether you have expressed interest in their profile
• Horoscope details and family background, if you choose to add them

Not visible to other members (without your explicit consent):
• Your phone number
• Your email address
• Your exact residential address
• Any payment or billing details
• Identity documents

You can adjust your profile visibility in your account settings. You may choose to hide your profile from search results at any time without deleting your account.`,
  },
  {
    id: 'sharing',
    title: '6. Who We Share Your Data With',
    content: `We share your information only in the limited circumstances described below:

Subsidiaries and partners: We may share your data with our subsidiaries, affiliates, and trusted partner companies for service improvement or marketing purposes, under appropriate confidentiality agreements.

Service providers: We work with trusted third-party companies who help us operate the Platform — including cloud hosting, mobile verification, payment processing, and fraud prevention. These providers access only the minimum data needed and are contractually bound to keep it confidential.

Fraud prevention and payment security: We may share data with fraud prevention agencies, payment gateway providers, or other third-party service providers to ensure transaction security.

Legal and regulatory requirements: We may disclose your information to law enforcement agencies, courts, or government bodies if we are legally required to do so, or if we believe disclosure is necessary to prevent serious harm or criminal activity.

Business restructuring: If the Company is acquired, merged, or undergoes a significant restructuring, your data may be transferred to the new entity under equivalent privacy commitments.

Please note that our Platform may contain links to third-party websites. We are not responsible for the content or privacy practices of those external sites.

We do not share your personal data with advertisers, data brokers, or any entity for their own commercial use.`,
  },
  {
    id: 'security',
    title: '7. How We Protect Your Data',
    content: `We implement organisational and technical security measures to protect your personal information:

• All data transmitted between your device and our servers is encrypted using TLS (Transport Layer Security)
• Sensitive data at rest is protected using industry-standard encryption
• Access to member data within our organisation is strictly limited to authorised personnel with a legitimate operational need
• We conduct regular reviews of our security practices and infrastructure
• Mobile number OTP verification reduces the risk of fake or fraudulent accounts
• We follow strict protocols to prevent unauthorised access or misuse once your data is received

While we strive to use commercially acceptable means to safeguard your data, no method of transmission over the internet is 100% secure. In the event of a data breach that is likely to affect your rights or privacy, we will notify you as required by applicable law.`,
  },
  {
    id: 'screenshot',
    title: '8. Screenshot & Screen Recording',
    content: `To enhance user privacy and protect sensitive personal information — including user profiles, family details, and photographs — our mobile applications disable screenshots and screen recordings starting from the login screen and throughout the entire app experience.

This measure is implemented solely to prevent unauthorised sharing or misuse of user data and is not intended to restrict your normal use of the Platform.

This restriction applies to:
• Profile pages of other members
• Personal photographs and contact details
• In-app messages and conversations
• All screens displaying member information

If you encounter any issue related to this feature, please contact us at info@weddyfyp.com.`,
  },
  {
    id: 'retention',
    title: '9. How Long We Keep Your Data',
    content: `We retain your personal data for as long as necessary to:

i. Fulfil the purpose for which it was collected
ii. Maintain an active subscription or account
iii. Resolve disputes and enforce agreements
iv. Comply with applicable laws
v. Conduct audits and support legitimate business activities

If you delete your account:
• Your profile and photos are removed from public view within 48 hours of your request
• Most personal data is permanently deleted within 30 days
• Certain records (such as payment transactions and abuse reports) may be retained for up to 7 years to comply with financial regulations or for the resolution of ongoing legal matters
• Backup copies of data are fully overwritten within 90 days of deletion

If you have not used the Platform for 24 consecutive months, we may deactivate your account and notify you before doing so.`,
  },
  {
    id: 'rights',
    title: '10. Your Rights',
    content: `You have the following rights regarding your personal data:

Right to access: You may request a summary of the personal data we hold about you.

Right to correction: If any of your information is incorrect or outdated, you can update it directly in your profile or contact us to request a correction.

Right to deletion: You can request that we delete your personal data. We will do so within 30 days, subject to any legal obligations that require us to retain certain records.

Right to object: You may object to certain types of processing, including receiving marketing communications.

Right to withdraw consent: Where our processing is based on your consent, you may withdraw it at any time. This does not affect the lawfulness of processing that occurred before withdrawal.

To exercise any of these rights, email us at info@weddyfyp.com with the subject line "Privacy Request". We will respond within 30 days.`,
  },
  {
    id: 'children',
    title: '11. Minors',
    content: `Weddyfyp is not intended for anyone under the age of 18. We do not knowingly collect or process personal data from persons below this age.

If you believe that a minor has created an account or submitted data on our Platform, please notify us immediately at info@weddyfyp.com. We will investigate and, if confirmed, delete the data without delay.`,
  },

  {
    id: 'childsafety',
    title: '12. Child Safety Standards',
    content: `Weddyfyp is committed to protecting children and preventing child sexual abuse and exploitation (CSAE). Our Platform strictly prohibits the sharing or promotion of any content that involves CSAE.

We have systems in place to:
• Detect, review, and take action against any content that violates child safety standards
• Remove such content immediately upon identification
• Report violations to relevant law enforcement authorities as required by law

If you encounter any content or behaviour on the Platform that you believe involves child sexual abuse or exploitation, please report it immediately:
• Using the in-app reporting feature on the relevant profile or message
• By emailing us directly at info@weddyfyp.com with the subject "Child Safety Report"

We comply with all relevant child safety laws and industry best practices to ensure a safe environment for all users. Any member found to be involved in the creation, distribution, or promotion of CSAE material will have their account permanently terminated and will be reported to the appropriate authorities.`,
  },
  {
    id: 'changes',
    title: '13. Changes to This Policy',
    content: `This Privacy Policy may be updated periodically to reflect changes in our practices or legal requirements. When we make significant changes, we will:

• Update the "Last updated" date at the top of this page
• Post updates on this page without prior notice for minor changes
• Send a notification to your registered email address for significant changes
• Display a visible notice within the Platform

If you continue to use Weddyfyp after changes to this Policy take effect, you accept the revised terms. We encourage you to review this page regularly to stay informed about how we protect your information.`,
  },
  {
    id: 'contact',
    title: '14. Grievance Redressal & Contact',
    content: `If you have any concerns regarding your personal data or our privacy practices, you may contact our Grievance Officer:

Grievance Officer: Mr. Manoj
Company: Roald Software Solutions Private Limited
Address: A/P P NO 11 & 12, 206, SRI S RESIDENCY, OLD BOWENPALLY, HYDERABAD 500011, India
Email: info@weddyfyp.com
Phone: +91 9490023820
Office Hours: Monday to Saturday, 10:00 AM to 6:00 PM IST (excluding Sundays and public holidays)

The Grievance Officer has been appointed in compliance with Section 5(9) of the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.

We are committed to resolving privacy concerns promptly and fairly. Formal grievances are addressed within 30 days in accordance with Indian IT regulations.

For general privacy questions or data requests, email us at info@weddyfyp.com with the subject line "Privacy Request".

This Privacy Policy was last updated on 28 May 2026.`,
  },
];

export default function Privacy() {
  const [active, setActive] = useState('intro');

  useEffect(() => {
    const handler = () => {
      const offsets = sections.map(s => {
        const el = document.getElementById(s.id);
        return el ? { id: s.id, top: el.getBoundingClientRect().top } : null;
      }).filter(Boolean);
      const visible = offsets.filter(o => o.top < 200);
      if (visible.length) setActive(visible[visible.length - 1].id);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div style={{ paddingTop: '70px' }}>
      <Helmet>
        <title>Privacy Policy - Weddyfyp Matrimony</title>
        <meta name="description" content="Weddyfyp's Privacy Policy explains how we collect, use, and protect your personal data. Your privacy is our priority." />
        <link rel="canonical" href="https://weddyfyp.com/privacy" />
      </Helmet>

      {/* ── Hero ── */}
      <section style={{ background: 'linear-gradient(150deg, #fdf5f8, #fdf5f8)', padding: '64px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 44px)', color: '#1c1917', marginBottom: '14px' }}>Privacy Policy</h1>
          <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '12px' }}>Last updated: 25 June 2026</p>
          <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: '1.75' }}>
            Your trust is the foundation of everything we do. This policy is our promise to you — plain language, no hidden clauses — about exactly how we handle your personal information.
          </p>
        </div>
      </section>

      {/* ── Content ── */}
      <section style={{ padding: '60px 24px 88px', background: '#fff' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: '260px 1fr', gap: '48px', alignItems: 'start' }}>

          {/* Sidebar */}
          <div style={{ position: 'sticky', top: '90px' }} className="privacy-sidebar">
            <div style={{ background: '#faf8f7', borderRadius: '16px', padding: '18px', border: '1px solid #f3f4f6', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
              <p style={{ fontSize: '10px', fontWeight: '800', color: '#9ca3af', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '14px' }}>SECTIONS</p>
              {sections.map(s => (
                <button key={s.id}
                  onClick={() => { document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActive(s.id); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '7px 12px', borderRadius: '8px', border: 'none',
                    background: active === s.id ? '#fce4ec' : 'transparent',
                    color: active === s.id ? '#C2185B' : '#6b7280',
                    fontSize: '12px', fontWeight: active === s.id ? '600' : '400',
                    cursor: 'pointer', marginBottom: '2px', transition: 'all 0.15s',
                    fontFamily: "'Inter', sans-serif",
                    lineHeight: '1.4',
                  }}>
                  {s.title}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px 20px', marginBottom: '36px', display: 'flex', gap: '12px' }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>🛡️</span>
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.65' }}>
                <strong>Our commitment:</strong> We never sell your personal data. Your information exists to help you find a life partner — nothing else.
              </p>
            </div>

            {sections.map(s => (
              <div key={s.id} id={s.id} style={{ marginBottom: '52px', scrollMarginTop: '96px' }}>
                <h2 style={{ fontSize: '21px', color: '#1c1917', marginBottom: '16px', paddingBottom: '12px', borderBottom: '2px solid #fce4ec' }}>
                  {s.title}
                </h2>
                <div style={{ fontSize: '15px', color: '#4b5563', lineHeight: '1.9', whiteSpace: 'pre-line' }}>
                  {s.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .privacy-sidebar { display: none !important; }
          section > div[style*="grid-template-columns"] { grid-template-columns: 1fr !important; }
        }
        .privacy-sidebar::-webkit-scrollbar { width: 4px; }
        .privacy-sidebar::-webkit-scrollbar-track { background: transparent; }
        .privacy-sidebar::-webkit-scrollbar-thumb { background: #f8bbd0; border-radius: 4px; }
      `}</style>
    </div>
  );
}
